import { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import '@react-pdf-viewer/core/lib/styles/index.css';
import HeaderCard from '../../../components/Header/HeaderCard';
import EmployeeNav from './EmployeePayslipNav';
import Payslips from './Payslips';
import { apiService } from '../../../components/Config/api.service';
import PayslipHeader from './PayslipHeader';
import { getEmployeesForAction } from './PayslipUtils';
import { canEditHrData, canViewAccountingJournal, isAccountant } from '../../../components/Auth/permissions';
import {
    FaHistory,
    FaMailBulk,
    FaCalculator,
    FaPrint,
    FaLock,
    FaCalendarWeek,
    FaFileExcel,
    FaUserTie,
    FaChartLine
} from 'react-icons/fa';
import BulletinTab from './PayslipBulletin';
import { notify } from '../../../components/Utils/Notifications';
import PayslipConfirmModal from './PayslipConfirmModal';
import CloseMonthWarningsModal from './CloseMonthWarningsModal';
import PayslipProgressModal from './PayslipProgressModal';
import AnnualPayslipView from './AnnualPayslipView';
import AccountingJournal from '../Compta/AccountingJournal';
import Comptability from '../Compta/Comptability';
import ExternalPayslipsMonthly from './ExternalPayslipsMonthly';
import PayslipVariationReport from './PayslipVariationReport'; // Add this import

const getLastDayOfMonth = (yearMonth) => {
    const [year, month] = yearMonth.split('-');
    return new Date(year, month, 0).toISOString().split('T')[0];
};

const formatMonthYear = (yearMonth, locale = 'fr-FR') => {
    const [year, month] = yearMonth.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
};

const getFirstDayOfMonth = (yearMonth) => `${yearMonth}-01`;

const formatPeriodDate = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(`${dateValue}T00:00:00`);
    return Number.isNaN(date.getTime())
        ? dateValue
        : date.toLocaleDateString('fr-FR');
};

const getEmployeeFirstName = (employeeName) => {
    const normalizedName = String(employeeName || '').trim();
    if (!normalizedName) return "collaborateur";
    return normalizedName.split(/\s+/)[0];
};

const buildPayslipEmailHtml = ({ employeeName, dateFrom, dateTo }) => `
    <p>Cher(e) ${getEmployeeFirstName(employeeName)},</p>
    <p>Veuillez trouver ci-joint votre bulletin de salaire pour la période du ${formatPeriodDate(dateFrom)} au ${formatPeriodDate(dateTo)}.</p>
    <p>N'hésitez pas à contacter votre responsable RH si vous avez des questions ou des préoccupations concernant votre bulletin.</p>
    <p>Bien cordialement,</p>
`;

const shiftMonth = (yearMonth, delta) => {
    const [year, month] = yearMonth.split('-').map(Number);
    const shiftedDate = new Date(year, month - 1 + delta, 1);
    return `${shiftedDate.getFullYear()}-${String(shiftedDate.getMonth() + 1).padStart(2, '0')}`;
};

const pdfBlobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

const parseCcEmails = (ccValue) => {
    if (!ccValue) return [];
    if (Array.isArray(ccValue)) {
        return ccValue.map((v) => String(v || '').trim()).filter(Boolean);
    }
    return String(ccValue)
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
};

const mergeCcEmails = (...values) => {
    const merged = [...new Set(values.flatMap(parseCcEmails))];
    return merged.length ? merged.join(',') : undefined;
};

const sendPayslipEmail = async ({ to, cc, subject, html, template_id, params, filename, base64content }) => {
    await apiService.sendEmail({
        to,
        cc,
        subject,
        html,
        template_id,
        params,
        attachment: {
            filename,
            content: base64content,
        }
    });
};

function PayslipsReport() {
    const { t, i18n } = useTranslation();
    const dateLocale = i18n.resolvedLanguage === 'en' ? 'en-US' : 'fr-FR';
    const navigate = useNavigate();
    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user') || '{}');
        } catch {
            return {};
        }
    }, []);
    const activeCompany = user?.company_active;
    const hasDailySalaryInput = activeCompany?.has_daily_salary_input || false;

    const [activeTab, setActiveTab] = useState("Bulletin");
    // Le comptable n'a que la vue Journal & Répartition ; les rôles en consultation
    // seule ne voient pas les actions d'écriture (calcul, envoi, saisie).
    const accountantOnly = isAccountant();
    const canEdit = canEditHrData();
    const canViewJournal = canViewAccountingJournal();
    const [activeView, setActiveView] = useState(accountantOnly ? "journal" : "monthly"); // 'monthly' or 'annual'
    const [payslipData, setPayslipData] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [report, setReport] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [employeeId, setEmployeeId] = useState('');
    const [contractId, setContractId] = useState('');
    const [filteredEmployees, setFilteredEmployees] = useState([]);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [modalSelectedEmployees, setModalSelectedEmployees] = useState([]);
    const [isMonthClosed, setIsMonthClosed] = useState(false);
    // Les routes Odoo `type='json'` répondent toujours HTTP 200 : la valeur du
    // contrôleur arrive dans `result`, et un `return {...}, 409` y devient
    // `[{...}, 409]`. Le code de statut n'atteint donc jamais axios — un refus
    // métier se présente comme une réponse réussie. Il faut lire la charge
    // utile pour le détecter, sans quoi le refus reste invisible (#15).
    const readJsonRpcPayload = (responseData) => {
        const result = responseData?.result ?? responseData;
        return Array.isArray(result) ? result[0] : result;
    };
    const [isJournalConfirmOpen, setIsJournalConfirmOpen] = useState(false);
    // Avertissements comptables renvoyés par la clôture, en attente de confirmation.
    const [closeWarnings, setCloseWarnings] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [historyMaxMonth, setHistoryMaxMonth] = useState(shiftMonth(new Date().toISOString().slice(0, 7), -1));
    const [manualCc, setManualCc] = useState('');
    const [maxAllowedMonth, setMaxAllowedMonth] = useState(new Date().toISOString().slice(0, 7));
    const [monthLockEnabled, setMonthLockEnabled] = useState(true);
    const [showResetPendingOnClosedMonth, setShowResetPendingOnClosedMonth] = useState(false);

    const currentMonth = useMemo(() => new Date().toISOString().slice(0, 7), []);

    const refreshEmployeeList = () => setRefreshTrigger(t => t + 1);

    const handleMonthChange = useCallback((e) => setSelectedMonth(e.target.value), []);
    const handleYearChange = useCallback((e) => setSelectedYear(parseInt(e.target.value, 10)), []);

    const handlePayslipDataLoad = useCallback((data) => {
        setPayslipData(data);
        setIsLoading(false);
    }, []);

    const resolveActivePayrollMonth = useCallback(async () => {
        let activeMonth = currentMonth;
        let lockEnabled = true;

        try {
            const response = await apiService.getElement('/payslip/active_month');
            if (response?.data?.active_month) {
                activeMonth = response.data.active_month;
            }
            if (response?.data?.month_lock_enabled !== undefined) {
                lockEnabled = response.data.month_lock_enabled;
            }
        } catch (error) {
            console.error('Erreur lors de la récupération du mois à clôturer:', error);
        }

        setMonthLockEnabled(lockEnabled);
        // Blocage désactivé : on ne plafonne plus le sélecteur (navigation jusqu'au mois
        // courant), mais le mois affiché par défaut reste inchangé pour ne masquer aucun
        // bulletin des employés en cours de saisie.
        setMaxAllowedMonth(lockEnabled ? activeMonth : currentMonth);
        setSelectedMonth(activeMonth);
        setHistoryMaxMonth(shiftMonth(activeMonth, -1));
    }, [currentMonth]);

    useEffect(() => {
        resolveActivePayrollMonth();
    }, [resolveActivePayrollMonth]);

    useEffect(() => {
        apiService.getElement('/company/info')
            .then((res) => setShowResetPendingOnClosedMonth(Boolean(res?.data?.show_reset_pending_on_closed_month)))
            .catch(() => setShowResetPendingOnClosedMonth(false));
    }, []);

    // SUPPRIMER CETTE FONCTION, ELLE N'EST PLUS NÉCESSAIRE
    // const findPayslipForEmployees = async (employeesList, month = selectedMonth) => { ... };

    const handleCalculate = useCallback(() => {
        if (!employeeId) {
            alert(t('payslipReport.selectEmployeeAlert'));
            return;
        }
        if (!contractId) {
            alert(t('payslipReport.contractNotFoundAlert'));
            return;
        }
        setIsLoading(true);
        setPayslipData(null);
        const newPayslip = {
            employee_id: employeeId,
            contract_id: contractId,
            date_from: `${selectedMonth}-01`,
            date_to: getLastDayOfMonth(selectedMonth),
            name: `Bulletin de ${report?.Employee || "l'employé"} - ${formatMonthYear(selectedMonth)}`,
            state: 'verify'
        };
        console.log('Nouveau bulletin à créer:', newPayslip);
        apiService.saveElement('/payslip', newPayslip)
            .then(response => {
                const payslipId = response.data.result.payslip_id;
                setReport(prev => ({
                    ...prev,
                    id: payslipId,
                    State: 'verify'
                }));
                console.log('Bulletin créé avec succès:', payslipId);
                return apiService.getElement(`/payslip/json/${payslipId}`);
            })
            .then(response => {
                if (response && response.data) {
                    setPayslipData(response.data);
                }
                refreshEmployeeList();
            })
            .catch(error => {
                console.error('Erreur lors du calcul du bulletin:', error);
                alert(t('payslipReport.calculateErrorAlert'));
            })
            .finally(() => setIsLoading(false));
    }, [employeeId, contractId, selectedMonth, report]);

    const handleRecalculate = useCallback(() => {
        if (!report || !report.id) {
            alert(t('payslipReport.noPayslipToRecalculateAlert'));
            return;
        }
        setIsLoading(true);
        apiService.updateElementById('/payslip', report.id, { state: 'verify' })
            .then(() => apiService.getElement(`/payslip/json/${report.id}`))
            .then(response => {
                if (response && response.data) {
                    setPayslipData(response.data);
                    setReport(prev => ({ ...prev, State: 'verify' }));
                }
                refreshEmployeeList();
            })
            .catch(error => {
                console.error('Erreur lors du recalcul du bulletin:', error);
                alert(t('payslipReport.recalculateErrorAlert'));
            })
            .finally(() => setIsLoading(false));
    }, [report]);

    const handleCalculateAllFilteredPayslips = async (employees, month) => {
        const toProcess = employees.filter(emp => emp.payslip_state !== 'done' && emp.payslip_state !== 'cancel');

        if (toProcess.length === 0) {
            notify(t('payslipReport.noPayslipToCalculate'), "top-right", 2);
            return;
        }

        setIsLoading(true);
        setProgress({ current: 0, total: toProcess.length });
        let done = 0;
        // Les échecs étaient jusqu'ici uniquement `console.error` : le compteur
        // avançait quand même et le message final annonçait « calcul terminé ».
        // L'opérateur croyait que tout était passé, alors qu'un employé pouvait
        // avoir été refusé. On collecte les échecs pour les restituer (#15).
        const failures = [];

        for (const emp of toProcess) {
            try {
                if (emp.payslip_id) {
                    // Recalculer un bulletin existant
                    await apiService.updateElementById('/payslip', emp.payslip_id, { state: 'verify' });
                } else {
                    // Créer un nouveau bulletin
                    const newPayslip = {
                        employee_id: emp.id,
                        contract_id: emp.contract_id,
                        date_from: `${month}-01`,
                        date_to: getLastDayOfMonth(month),
                        name: `Bulletin de ${emp.name || "l'employé"} - ${formatMonthYear(month)}`,
                        state: 'verify'
                    };
                    const response = await apiService.saveElement('/payslip', newPayslip);
                    const payload = readJsonRpcPayload(response?.data);
                    if (payload?.error) {
                        // Refus métier : le backend renvoie désormais le motif
                        // au lieu du générique « An error occurred… ». On ne le
                        // met pas en avant, on cesse simplement d'annoncer un
                        // succès pour un salarié qui n'a pas été traité.
                        failures.push({ name: emp.name, reason: payload.error });
                    }
                }
            } catch (err) {
                console.error(`Erreur lors du calcul du bulletin pour ${emp.name}:`, err);
                failures.push({
                    name: emp.name,
                    reason: err?.userMessage || '',
                });
            }
            done++;
            setProgress(prev => ({ ...prev, current: done }));
        }

        if (failures.length > 0) {
            notify(
                t('payslipReport.calculationDoneWithFailures', {
                    count: done - failures.length,
                    failed: failures.length,
                }),
                "top-right",
                3
            );
            failures.slice(0, 5).forEach(failure => {
                notify(`${failure.name} : ${failure.reason}`, "top-right", 3);
            });
        } else {
            notify(t('payslipReport.calculationDone', { count: done }), "top-right", 2);
        }
        setIsLoading(false);
        setProgress({ current: 0, total: 0 });
        refreshEmployeeList();
    };

    const handleCancelPayslip = useCallback(() => {
        if (!report || !report.id) {
            alert(t('payslipReport.noPayslipToCancelAlert'));
            return;
        }
        setIsLoading(true);
        apiService.updateElementById('/payslip', report.id, { state: 'cancel' })
            .then(() => {
                notify(t('payslipReport.cancelSuccess'), "top-right", 2);
                setReport(prev => ({ ...prev, State: 'cancel' }));
                refreshEmployeeList();
            })
            .catch(error => {
                console.error('Erreur lors de l\'annulation du bulletin:', error);
                notify(t('payslipReport.cancelError'), "top-right", 3);
            })
            .finally(() => setIsLoading(false));
    }, [report]);

    const handlePrintAllFilteredPayslips = async (employees, month) => {
        setIsLoading(true);
        try {
            const payslipIds = employees
                .map(emp => emp.payslip_id)
                .filter(id => !!id);

            if (payslipIds.length === 0) {
                notify(t('payslipReport.noPayslipToPrint'), "top-right", 3);
                setIsLoading(false);
                return;
            }

            const pdfResp = await apiService.getBlobFile(`/payslip/pdf?ids=${payslipIds.join(',')}`);
            if (pdfResp?.data) {
                const url = window.URL.createObjectURL(new Blob([pdfResp.data], { type: 'application/pdf' }));
                window.open(url, '_blank');
                notify(t('payslipReport.pdfGenerated', { count: payslipIds.length }), "top-right", 2);
            } else {
                notify(t('payslipReport.pdfGenerationError'), "top-right", 3);
            }
        } catch (e) {
            notify(t('payslipReport.bulkPrintError'), "top-right", 3);
            console.error(e);
        }
        setIsLoading(false);
    };

    const handleSendPayslip = useCallback(async () => {
        setIsLoading(true);
        setProgress({ current: 0, total: 1 });
        if (!report || !report.email || !String(report.email).trim()) {
            alert(t('payslipReport.emailNotConfiguredAlert'));
            setIsLoading(false);
            setProgress({ current: 0, total: 0 });
            return;
        }
        if (!payslipData) {
            alert(t('payslipReport.noPayslipToSendAlert'));
            setIsLoading(false);
            setProgress({ current: 0, total: 0 });
            return;
        }
        setIsLoading(true);
        try {
            const pdfResp = await apiService.getBlobFile(`/payslip/pdf/${report.id}`);
            const base64data = await pdfBlobToBase64(new Blob([pdfResp.data], { type: 'application/pdf' }));
            const employeeName = report.Employee || "l'employé";
            const dateFrom = report.De || getFirstDayOfMonth(selectedMonth);
            const dateTo = report.Vers || getLastDayOfMonth(selectedMonth);
            await sendPayslipEmail({
                to: report.email,
                cc: report.email_cc || report.cc_email || undefined,
                subject: `Votre bulletin - ${formatMonthYear(selectedMonth)}`,
                html: buildPayslipEmailHtml({ employeeName, dateFrom, dateTo }),
                template_id: 1,
                params: {
                    prenom: employeeName,
                    month: formatMonthYear(selectedMonth),
                },
                filename: `bulletin-${employeeName}.pdf`,
                base64content: base64data,
            });
            notify(t('payslipReport.sendSuccess'), "top-right", 2);
            setProgress({ current: 1, total: 1 });
        } catch (e) {
            console.error(e);
            notify(t('payslipReport.sendError'), "top-right", 3);
        }
        setIsLoading(false);
        setProgress({ current: 0, total: 0 });
    }, [payslipData, report, selectedMonth]);

    const handleSendAllFilteredPayslips = async (employees, month, groupCc) => {
        setIsLoading(true);
        setProgress({ current: 0, total: employees.length });
        let sent = 0;
        let skippedNoEmail = 0;

        for (const employee of employees) {
            if (!employee.payslip_id) {
                sent++;
                setProgress(prev => ({ ...prev, current: sent }));
                continue;
            }
            if (!employee.email || !String(employee.email).trim()) {
                skippedNoEmail++;
                sent++;
                setProgress(prev => ({ ...prev, current: sent }));
                continue;
            }
            try {
                const pdfResp = await apiService.getBlobFile(`/payslip/pdf/${employee.payslip_id}`);
                if (!pdfResp?.data) {
                    sent++;
                    setProgress(prev => ({ ...prev, current: sent }));
                    continue;
                }
                const blob = new Blob([pdfResp.data], { type: 'application/pdf' });
                const base64data = await pdfBlobToBase64(blob);
                const employeeName = employee.name || "l'employé";
                const dateFrom = employee.De || employee.date_from || getFirstDayOfMonth(month);
                const dateTo = employee.Vers || employee.date_to || getLastDayOfMonth(month);
                await sendPayslipEmail({
                    to: employee.email, // Assurez-vous que l'email est dans l'objet employé
                    cc: mergeCcEmails(employee.email_cc || employee.cc_email, groupCc),
                    subject: `Votre bulletin - ${formatMonthYear(month)}`,
                    html: buildPayslipEmailHtml({ employeeName, dateFrom, dateTo }),
                    template_id: 1,
                    params: {
                        prenom: employeeName,
                        month: formatMonthYear(month),
                    },
                    filename: `bulletin-${employeeName}.pdf`,
                    base64content: base64data,
                });
            } catch (error) {
                console.error(`Erreur lors de l'envoi du bulletin pour ${employee.name}:`, error);
                notify(
                    error.userMessage || t('payslipReport.sendErrorFor', { name: employee.name }),
                    "top-right",
                    3
                );
            }
            sent++;
            setProgress(prev => ({ ...prev, current: sent }));
        }
        if (skippedNoEmail > 0) {
            notify(t('payslipReport.sendDoneWithSkipped', { count: employees.length, skipped: skippedNoEmail }), "top-right", 2);
        } else {
            notify(t('payslipReport.sendDone', { count: employees.length }), "top-right", 2);
        }
        setIsLoading(false);
        setProgress({ current: 0, total: 0 });
    };

    const handleConfirm = () => {
        setShowConfirmModal(false);
        if (confirmAction === 'send') {
            handleSendAllFilteredPayslips(modalSelectedEmployees, selectedMonth, manualCc);
            setManualCc('');
        } else if (confirmAction === 'calculate') {
            handleCalculateAllFilteredPayslips(modalSelectedEmployees, selectedMonth);
        } else if (confirmAction === 'print') {
            handlePrintAllFilteredPayslips(modalSelectedEmployees, selectedMonth);
        } else if (confirmAction === 'close_month') {
            handleCloseMonth();
        }
        setConfirmAction(null);
    };

    const dropdownActions = useMemo(() => {
        const actions = [
            {
                label: t('payslipReport.annualView'),
                onClick: () => setActiveView(v => v === 'monthly' ? 'annual' : 'monthly'),
                icon: <FaCalendarWeek size={16} />,
                isActive: activeView === 'annual'
            },
            {
                label: t('payslipReport.payslipHistory'),
                onClick: () => setActiveTab("Historique"),
                icon: <FaHistory size={16} />
            },
            ...(canEdit ? [
                {
                    label: t('payslipReport.contractorStatement'),
                    onClick: () => setActiveView('external_contracts'),
                    icon: <FaUserTie size={16} />,
                    isActive: activeView === 'external_contracts'
                }
            ] : []),
            {
                label: t('payslipReport.variationReport'),
                onClick: () => setActiveView('variation'),
                icon: <FaChartLine size={16} />,
                isActive: activeView === 'variation'
            },
            ...(hasDailySalaryInput && canEdit ? [
                {
                    label: t('payslipReport.dailyWorkerInput'),
                    onClick: () => navigate('/paie/journaliers'),
                    icon: <FaFileExcel size={16} />
                }
            ] : []),
            {
                isSeparator: true
            },
        ];

        if (canViewJournal) {
            actions.push({
                color: 'bg-[--color-primary] text-white hover:text-[--color-primary]',
                label: t('payslipReport.accountingJournalAction'),
                onClick: () => setActiveView('journal'),
                icon: <FaFileExcel size={16} />,
                isActive: activeView === 'journal'
            });
        }

        if (!isMonthClosed && canEdit) {
            actions.push({
                label: t('payslipReport.calculatePayslips'),
                onClick: () => {
                    setModalSelectedEmployees(filteredEmployees);
                    setConfirmAction('calculate');
                    setShowConfirmModal(true);
                },
                icon: <FaCalculator size={16} />,
                disabled: isMonthClosed,
            });
        }

        actions.push({
            label: t('payslipReport.printPayslips'),
            onClick: () => {
                setModalSelectedEmployees(filteredEmployees);
                setConfirmAction('print');
                setShowConfirmModal(true);
            },
            icon: <FaPrint size={16} />
        });

        if (canEdit) {
            actions.push({
                label: t('payslipReport.sendPayslips'),
                onClick: () => {
                    setModalSelectedEmployees(filteredEmployees);
                    setConfirmAction('send');
                    setShowConfirmModal(true);
                },
                icon: <FaMailBulk size={16} />
            });
        }

        return actions;

    }, [filteredEmployees, activeView, isMonthClosed, setActiveTab, hasDailySalaryInput, navigate, t, canEdit, canViewJournal]);

    const getConfirmModalTitle = () => {
        switch (confirmAction) {
            case 'send': return t('payslipReport.bulkSendTitle');
            case 'calculate': return t('payslipReport.bulkCalculateTitle');
            case 'print': return t('payslipReport.bulkPrintTitle');
            case 'close_month': return t('payslipReport.closeMonthTitle');
            default: return "";
        }
    };

    const getConfirmModalSubtitle = () => {
        switch (confirmAction) {
            case 'send': return t('payslipReport.bulkSendSubtitle');
            case 'calculate': return t('payslipReport.bulkCalculateSubtitle');
            case 'print': return t('payslipReport.bulkPrintSubtitle');
            case 'close_month': return t('payslipReport.closeMonthSubtitle');
            default: return "";
        }
    };

    const getConfirmModalIcon = () => {
        switch (confirmAction) {
            case 'send': return <FaMailBulk className="text-[--color-primary]" size={20} />;
            case 'calculate': return <FaCalculator className="text-[--color-primary]" size={20} />;
            case 'print': return <FaPrint className="text-[--color-primary]" size={20} />;
            case 'close_month': return <FaLock className="text-[--color-primary]" size={20} />;
            default: return null;
        }
    };


    // confirmedPayslipIds : second appel, après confirmation des avertissements comptables.
    const handleCloseMonth = async (confirmedPayslipIds = null) => {
        setIsLoading(true);
        try {
            const verifyPayslipIds = confirmedPayslipIds || modalSelectedEmployees
                .filter(emp => emp.payslip_state === 'verify' && emp.payslip_id)
                .map(emp => emp.payslip_id);

            if (verifyPayslipIds.length === 0) {
                notify(t('payslipReport.noPayslipToClose'), "top-right", 2);
                setIsLoading(false);
                return;
            }

            const totalToProcess = verifyPayslipIds.length;
            setProgress({ current: 0, total: totalToProcess });
            const closeResponse = await apiService.saveElement('/payslip/close_month', {
                payslip_ids: verifyPayslipIds,
                confirm_accounting_warnings: !!confirmedPayslipIds,
            });
            const closePayload = readJsonRpcPayload(closeResponse?.data);
            if (closePayload?.requires_confirmation) {
                setCloseWarnings({ ...closePayload, payslipIds: verifyPayslipIds });
                setIsLoading(false);
                setProgress({ current: 0, total: 0 });
                return;
            }
            if (closePayload?.error) {
                notify(closePayload.error, "top-right", 3);
                setIsLoading(false);
                setProgress({ current: 0, total: 0 });
                return;
            }
            setProgress({ current: totalToProcess, total: totalToProcess });

            notify(t('payslipReport.closeMonthSuccess', { count: verifyPayslipIds.length }), "top-right", 2);
            refreshEmployeeList();
            await resolveActivePayrollMonth();
        } catch (e) {
            if (e.response?.status === 403) {
                notify(t('employeeDetails.insufficientRights'), "top-right", 3);
            } else {
                notify(t('payslipReport.closeMonthError'), "top-right", 3);
            }
            console.error(e);
        }
        setIsLoading(false);
        setProgress({ current: 0, total: 0 });
    };

    // SUPPRIMER CE USEEFFECT, L'INFO VIENT MAINTENANT DE EMPLOYEENAV
    // useEffect(() => {
    //     const fetchMonthStatus = async () => {
    //         try {
    //             const res = await apiService.getElement(`/payslip/month_status/${selectedMonth}`);
    //             setIsMonthClosed(res.data.is_closed);                
    //         } catch (e) {
    //             setIsMonthClosed(false);
    //         }
    //     };
    //     fetchMonthStatus();
    // }, [selectedMonth]);

    // Filtrer les employés en attente si action close_month ou calculate
    const employeesToShow = useMemo(
        () => getEmployeesForAction(confirmAction, filteredEmployees), // On retire le 3ème argument obsolète
        [confirmAction, filteredEmployees]
    );

    const selectedCount = modalSelectedEmployees.filter(emp => employeesToShow.some(e => e.id === emp.id)).length;
    const totalCount = employeesToShow.length;

    return (
        <div className="">
            {/* Modal de confirmation avec le composant PayslipConfirmModal */}
            <PayslipConfirmModal
                isOpen={showConfirmModal}
                onClose={() => { setShowConfirmModal(false); setConfirmAction(null); setManualCc(''); }}
                title={getConfirmModalTitle()}
                subtitle={getConfirmModalSubtitle()}
                headerIcon={getConfirmModalIcon()}
                employeesToShow={employeesToShow}
                modalSelectedEmployees={modalSelectedEmployees}
                setModalSelectedEmployees={setModalSelectedEmployees}
                selectedCount={selectedCount}
                totalCount={totalCount}
                handleConfirm={handleConfirm}
                isLoading={isLoading}
                confirmAction={confirmAction}
                manualCc={manualCc}
                setManualCc={setManualCc}
            />

            <CloseMonthWarningsModal
                warnings={closeWarnings}
                onClose={() => setCloseWarnings(null)}
                onConfirm={() => {
                    const payslipIds = closeWarnings.payslipIds;
                    setCloseWarnings(null);
                    handleCloseMonth(payslipIds);
                }}
                isLoading={isLoading}
            />

            {/* Modal de progression avec le composant Modal */}
            <PayslipProgressModal progress={progress} />

            {/* Modals for Journal Comptable */}
            <AccountingJournal
                isOpen={isJournalConfirmOpen}
                onClose={() => setIsJournalConfirmOpen(false)}
                selectedMonth={selectedMonth}
            />

            {/* Header principal */}
            <div className="flex justify-between items-start gap-6">
                {activeTab === "Bulletin" && activeView === "annual" ? (
                    <HeaderCard
                        title={t('payslipReport.annualViewTitle')}
                        text={t('payslipReport.defaultSubtitle')}
                    />
                ) : activeTab === "Bulletin" && activeView === "journal" ? (
                    <HeaderCard
                        title={t('payslipReport.journalViewTitle')}
                        text={t('payslipReport.defaultSubtitle')}
                    />
                ) : activeTab === "Bulletin" && activeView === "external_contracts" ? (
                    <HeaderCard
                        title={t('payslipReport.externalContractsTitle')}
                        text={t('payslipReport.externalContractsSubtitle')}
                    />
                ) : activeTab === "Bulletin" && activeView === "variation" ? (
                    <HeaderCard
                        title={t('payslipReport.variationViewTitle')}
                        text={t('payslipReport.variationViewSubtitle')}
                    />
                ) : activeTab === "Bulletin" && (
                    <HeaderCard
                        title={t('payslipReport.defaultTitle')}
                        text={t('payslipReport.defaultSubtitle')}
                    />
                )}
                {activeTab === 'Bulletin' && (
                    <div className="flex flex-col items-end gap-2">
                        <PayslipHeader
                            selectedMonth={selectedMonth}
                            handleMonthChange={handleMonthChange}
                            selectedYear={selectedYear}
                            handleYearChange={handleYearChange}
                            isLoading={isLoading}
                            filteredEmployees={filteredEmployees}
                            onCloseMonth={() => {
                                const employeesToClose = filteredEmployees.filter(emp => emp.payslip_state === 'verify');
                                setConfirmAction('close_month');
                                setShowConfirmModal(true);
                                setModalSelectedEmployees(employeesToClose);
                            }}
                            dropdownActions={dropdownActions}
                            isMonthClosed={isMonthClosed} // Ajouté pour passer le statut
                            activeView={activeView}
                            setActiveView={setActiveView}
                            allowMonthlyView={!accountantOnly}
                            monthInputDisabled={false}
                            monthInputMax={activeView === 'monthly' ? maxAllowedMonth : currentMonth}
                        />
                        {activeView === 'monthly' && monthLockEnabled && selectedMonth !== currentMonth && (
                            <p className="text-xs text-orange-600">
                                {t('payslipReport.currentMonthLocked', { month: formatMonthYear(selectedMonth, dateLocale) })}
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* Contenu principal */}
            {activeView === 'annual' && activeTab === 'Bulletin' ? (
                <AnnualPayslipView year={selectedYear} />
            ) : activeView === 'journal' ? (
                <Comptability
                    month={selectedMonth}
                    year={selectedYear}
                    setIsJournalConfirmOpen={setIsJournalConfirmOpen}
                />
            ) : activeView === 'external_contracts' ? (
                <ExternalPayslipsMonthly selectedMonth={selectedMonth} />
            ) : activeView === 'variation' ? (
                <PayslipVariationReport month={selectedMonth} />
            ) : (
                <div className="flex gap-4">
                    {activeTab === "Bulletin" && (
                        <div className="w-80 flex-shrink-0">
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <div className="flex gap-3">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-800">{t('payslipReport.collaborators')}</h3>
                                            <p className="text-sm text-gray-600">{t('payslipReport.selectEmployee')}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-2">
                                    <EmployeeNav
                                        setPayslipData={handlePayslipDataLoad}
                                        setIsloading={setIsLoading}
                                        setReport={setReport}
                                        selectedMonth={selectedMonth}
                                        setEmployeeId={setEmployeeId}
                                        setContractId={setContractId}
                                        setFilteredEmployees={setFilteredEmployees}
                                        setIsMonthClosed={setIsMonthClosed}
                                        refreshTrigger={refreshTrigger}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={`${activeTab === "Bulletin" ? "flex-1" : "w-full"}`}>
                        <div className="">
                            <div className="">
                                {activeTab === "Bulletin" ? (
                                    <BulletinTab
                                        Report={report}
                                        isLoading={isLoading}
                                        payslipData={payslipData}
                                        handleCalculate={handleCalculate}
                                        handleRecalculate={handleRecalculate}
                                        handleSendPayslip={handleSendPayslip}
                                        employeeId={employeeId}
                                        handleCancelPayslip={handleCancelPayslip}
                                        isMonthClosed={isMonthClosed}
                                        showResetPendingOnClosedMonth={showResetPendingOnClosedMonth}
                                        onPdfLoaded={() => setIsLoading(false)}
                                    />
                                ) : (
                                    <Payslips
                                        employeeId={employeeId}
                                        setActiveTab={setActiveTab}
                                        historyMaxMonth={historyMaxMonth}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PayslipsReport;
